import type { Socket } from 'socket.io-client';

import {
  type TaskProviderLike,
  type TokenUsage,
  type ToolUsage,
  RooCodeEventName,
} from '@roo-code/types';

import {
  type ExtensionInstance,
  type ExtensionBridgeControlChannelMessage,
  type ExtensionBridgeEventChannelMessage,
  TaskStatus,
  ExtensionBridgeControlChannelMessageType,
  ExtensionBridgeEventChannelMessageType,
  HEARTBEAT_INTERVAL_MS,
} from '../types.js';

export class ExtensionManager {
  private instanceId: string;
  private userId: string;
  private provider: TaskProviderLike;
  private publishEvent: (
    message: ExtensionBridgeEventChannelMessage,
  ) => Promise<boolean>;

  private extensionInstance: ExtensionInstance;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor(
    instanceId: string,
    userId: string,
    provider: TaskProviderLike,
    publishEvent: (
      message: ExtensionBridgeEventChannelMessage,
    ) => Promise<boolean>,
  ) {
    this.instanceId = instanceId;
    this.userId = userId;
    this.provider = provider;
    this.publishEvent = publishEvent;

    this.extensionInstance = {
      instanceId: this.instanceId,
      userId: this.userId,
      workspacePath: this.provider.cwd,
      extensionVersion:
        this.provider.context?.extension?.packageJSON?.version || 'unknown',
      lastHeartbeat: Date.now(),
      ...this.getCurrentTask(),
    };

    this.setupProviderListeners();
  }

  public async onConnect(socket: Socket): Promise<void> {
    await this.registerInstance(socket);
    this.startHeartbeat(socket);
  }

  public onDisconnect(): void {
    this.stopHeartbeat();
  }

  public async onReconnect(socket: Socket): Promise<void> {
    await this.registerInstance(socket);
    this.startHeartbeat(socket);
  }

  public async cleanup(socket: Socket | null): Promise<void> {
    this.stopHeartbeat();

    if (socket) {
      await this.unregisterInstance(socket);
    }
  }

  public getExtensionInstance(): ExtensionInstance {
    return this.extensionInstance;
  }

  public updateTask(taskId: string, status: TaskStatus): void {
    this.extensionInstance.taskId = taskId;
    this.extensionInstance.taskStatus = status;
  }

  public handleControlMessage(
    message: ExtensionBridgeControlChannelMessage,
  ): void {
    if (message.instanceId !== this.instanceId) {
      console.log(
        '[ExtensionManager] Ignoring control message for different instance:',
        {
          messageInstanceId: message.instanceId,
          ourInstanceId: this.instanceId,
        },
      );

      return;
    }

    console.log('[ExtensionManager] Processing control message:', {
      type: message.type,
      instanceId: message.instanceId,
    });

    switch (message.type) {
      case ExtensionBridgeControlChannelMessageType.StartTask: {
        console.log('[ExtensionManager] Starting task with:', {
          text: message.payload.text?.substring(0, 100) + '...',
          hasImages: !!message.payload.images,
        });

        this.provider.initClineWithTask(
          message.payload.text,
          message.payload.images,
        );

        break;
      }
      case ExtensionBridgeControlChannelMessageType.StopTask: {
        console.log('[ExtensionManager] Stopping task');
        const { taskId, taskStatus } = this.getCurrentTask();

        if (taskStatus === TaskStatus.Running) {
          console.log('[ExtensionManager] Canceling running task');
          this.provider.cancelTask();
          this.provider.postStateToWebview();
        } else if (taskId) {
          console.log('[ExtensionManager] Clearing task');
          this.provider.clearTask();
          this.provider.postStateToWebview();
        }
        break;
      }
    }
  }

  private async registerInstance(socket: Socket): Promise<void> {
    console.log('[ExtensionManager] Registering instance:', {
      instanceId: this.instanceId,
      userId: this.userId,
      taskId: this.extensionInstance.taskId,
      taskStatus: this.extensionInstance.taskStatus,
    });

    this.extensionInstance = {
      ...this.extensionInstance,
      lastHeartbeat: Date.now(),
      ...this.getCurrentTask(),
    };

    try {
      socket.emit('extension:register', this.extensionInstance);
      console.log('[ExtensionManager] emit() -> extension:register');
    } catch (error) {
      console.error(
        `[ExtensionManager] emit() failed -> extension:register: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      return;
    }

    await this.publishEvent({
      type: ExtensionBridgeEventChannelMessageType.InstanceRegistered,
      instanceId: this.instanceId,
      payload: { instance: this.extensionInstance },
      timestamp: Date.now(),
    });
  }

  private async unregisterInstance(socket: Socket): Promise<void> {
    console.log('[ExtensionManager] Unregistering instance:', this.instanceId);

    await this.publishEvent({
      type: ExtensionBridgeEventChannelMessageType.InstanceUnregistered,
      instanceId: this.instanceId,
      payload: { instanceId: this.instanceId },
      timestamp: Date.now(),
    });

    try {
      socket.emit('extension:unregister', { instanceId: this.instanceId });
      console.log('[ExtensionManager] emit() -> extension:unregister');
    } catch (error) {
      console.error(
        `[ExtensionManager] emit() failed -> extension:unregister: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  }

  private startHeartbeat(socket: Socket): void {
    this.stopHeartbeat();
    console.log('[ExtensionManager] Starting heartbeat interval');

    this.heartbeatInterval = setInterval(async () => {
      this.extensionInstance.lastHeartbeat = Date.now();
      const { taskId, taskStatus } = this.getCurrentTask();
      this.extensionInstance.taskId = taskId;
      this.extensionInstance.taskStatus = taskStatus;

      try {
        socket.emit('extension:heartbeat', this.extensionInstance);
        console.log('[ExtensionManager] emit() -> extension:heartbeat');
      } catch (error) {
        console.error(
          `[ExtensionManager] emit() failed -> extension:heartbeat: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }

      await this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.HeartbeatUpdated,
        instanceId: this.instanceId,
        payload: { instance: this.extensionInstance },
        timestamp: Date.now(),
      });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      console.log('[ExtensionManager] Stopping heartbeat interval');
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private setupProviderListeners(): void {
    console.log('[ExtensionManager] Setting up provider event listeners');

    this.provider.on(RooCodeEventName.TaskCreated, async (_task) => {
      console.log('[ExtensionManager] Task created event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskCreated,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(RooCodeEventName.TaskStarted, async (_taskId) => {
      console.log('[ExtensionManager] Task started event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskStarted,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(
      RooCodeEventName.TaskCompleted,
      async (_taskId: string, tokenUsage: TokenUsage, toolUsage: ToolUsage) => {
        console.log('[ExtensionManager] Task completed event');
        const payload = this.getCurrentTask();

        this.publishEvent({
          type: ExtensionBridgeEventChannelMessageType.TaskCompleted,
          instanceId: this.instanceId,
          payload: { ...payload, tokenUsage, toolUsage },
          timestamp: Date.now(),
        });
      },
    );

    this.provider.on(RooCodeEventName.TaskAborted, async () => {
      console.log('[ExtensionManager] Task aborted event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskAborted,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(RooCodeEventName.TaskFocused, async (_taskId) => {
      console.log('[ExtensionManager] Task focused event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskFocused,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(RooCodeEventName.TaskUnfocused, async (_taskId) => {
      console.log('[ExtensionManager] Task unfocused event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskUnfocused,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(RooCodeEventName.TaskActive, async (_taskId) => {
      console.log('[ExtensionManager] Task active event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskActive,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    this.provider.on(RooCodeEventName.TaskIdle, async (_taskId) => {
      console.log('[ExtensionManager] Task idle event');
      const payload = this.getCurrentTask();

      this.publishEvent({
        type: ExtensionBridgeEventChannelMessageType.TaskIdle,
        instanceId: this.instanceId,
        payload,
        timestamp: Date.now(),
      });
    });

    console.log('[ExtensionManager] Provider listeners setup complete');
  }

  private getCurrentTask(): { taskId: string; taskStatus: TaskStatus } {
    const task = this.provider?.getCurrentCline();

    if (task) {
      return {
        taskId: task.taskId,
        taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
      };
    } else {
      return { taskId: '', taskStatus: TaskStatus.None };
    }
  }
}
